import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeUp, stagger } from '@/lib/motion';
import { Section, SectionTitle, AuthorityBadge } from '@/components/shared/Section';
import { ArrowRight, MessageCircle, Database, Puzzle, Shield, Zap, Server, TrendingUp, Bot, Clock, ShieldCheck, RefreshCw, DollarSign, UserMinus, ShoppingCart, Landmark, HeartPulse, HardHat, CheckCircle, Users, Building2, Factory, Store, Truck, Shrimp, FileText, Presentation, Briefcase } from 'lucide-react';

const benefits = [
  { icon: Database, title: 'Sin licencias propietarias', desc: 'OpenClaw es open-source y gratuito. Sin costos de licencia recurrentes.', tag: 'OpenClaw' },
  { icon: Server, title: 'Data lake centralizado', desc: 'Todos tus datos en un solo lugar, gobernados y listos para activar.', tag: 'OpenClaw' },
  { icon: Clock, title: 'Trabaja 24/7', desc: 'Agente autónomo que procesa datos sin pausa y sin intervención.', tag: 'OpenClaw' },
  { icon: Users, title: 'Compañero de trabajo IA', desc: 'Claude Cowork planifica, ejecuta y reporta tareas de forma autónoma.', tag: 'Cowork' },
  { icon: FileText, title: 'Productividad de equipos', desc: 'Documentos, análisis, presentaciones y reportes generados automáticamente.', tag: 'Cowork' },
  { icon: Briefcase, title: 'Enterprise-ready', desc: 'Plugins por departamento: HR, Finanzas, TI, Ingeniería, Diseño.', tag: 'Cowork' },
];

const agentFeatures = [
  { icon: Bot, title: 'Agente autónomo de datos', desc: 'Procesa, organiza y distribuye datos sin intervención humana. Ejecuta tareas, detecta anomalías y notifica.', tag: 'OpenClaw' },
  { icon: Users, title: 'Compañero virtual de trabajo', desc: 'Completa tareas de conocimiento de forma autónoma: investiga, analiza, redacta y entrega resultados.', tag: 'Cowork' },
  { icon: MessageCircle, title: 'Le puedes hablar', desc: 'Lenguaje natural por WhatsApp, Telegram o escritorio. Pregunta lo que necesites y obtén respuestas en segundos.', tag: 'Ambos' },
  { icon: Clock, title: 'Trabaja 24/7/365', desc: 'No duerme, no se enferma. Procesa datos y completa tareas continuamente, sin pausas.', tag: 'OpenClaw' },
  { icon: Presentation, title: 'Crea documentos y presentaciones', desc: 'Reportes, análisis, propuestas y presentaciones ejecutivas generadas automáticamente.', tag: 'Cowork' },
  { icon: UserMinus, title: 'Reduce dependencia de personas', desc: 'Tareas que requieren 2-3 analistas las ejecutan estos agentes de forma automática. Libera a tu equipo.', tag: 'Ambos' },
  { icon: ShieldCheck, title: 'Gobernanza automática', desc: 'Aplica reglas de calidad, acceso y cumplimiento de forma autónoma. Datos siempre limpios y confiables.', tag: 'OpenClaw' },
  { icon: DollarSign, title: 'ROI desde el mes 1', desc: 'Ahorro en horas-persona, errores evitados y velocidad de decisión genera retorno inmediato.', tag: 'Ambos' },
];

const industries = [
  { icon: ShoppingCart, title: 'Retail & Supermercados', desc: 'Centralizar datos de ventas, merma, inventario, ESL y autoservicio.' },
  { icon: Landmark, title: 'Banca & Seguros', desc: 'Gobernanza de datos regulada, reportería automatizada, compliance.' },
  { icon: HeartPulse, title: 'Salud', desc: 'Datos de pacientes, operación clínica e interoperabilidad con estándares HL7.' },
  { icon: HardHat, title: 'Minería & Industria', desc: 'IoT masivo, telemetría, pesaje y predicción de fallas en terreno.' },
  { icon: Building2, title: 'Empresas Privadas', desc: 'Consolidar datos de operación, finanzas, RRHH y clientes en un solo data lake.' },
  { icon: Factory, title: 'Empresas Productivas', desc: 'Datos de producción, calidad, cadena de suministro y trazabilidad en tiempo real.' },
  { icon: Store, title: 'Medianas Empresas & PyMEs', desc: 'Automatizar reportería, eliminar planillas manuales y activar datos.' },
  { icon: Truck, title: 'Logística & Distribución', desc: 'Rastreo de flota, optimización de rutas, inventarios y predicción de demanda.' },
];

const steps = [
  { step: '01', title: 'Diagnóstico de datos', desc: 'Mapeamos tus fuentes, volúmenes, formatos y necesidades de integración.' },
  { step: '02', title: 'Arquitectura', desc: 'Diseñamos la arquitectura del data lake adaptada a tu infraestructura.' },
  { step: '03', title: 'Implementación', desc: 'Desplegamos OpenClaw + Cowork, conectamos fuentes y configuramos pipelines.' },
  { step: '04', title: 'Gobernanza', desc: 'Definimos políticas de acceso, calidad, linaje y cumplimiento.' },
  { step: '05', title: 'Activación', desc: 'Conectamos con dashboards, modelos IA, Cowork y herramientas de negocio.' },
];

const plans = [
  { name: 'Plan Diagnóstico', price: '8', desc: 'Evaluación completa de tu ecosistema de datos y roadmap de implementación.',
    includes: ['Mapeo de fuentes de datos', 'Evaluación de infraestructura actual', 'Informe de factibilidad y arquitectura', 'Recomendación de roadmap', 'Presentación ejecutiva'],
    cta: 'Solicitar diagnóstico' },
  { name: 'Plan Implementación', price: '35', desc: 'Despliegue completo de OpenClaw + Cowork con conexión de fuentes y gobernanza.',
    includes: ['Todo lo del diagnóstico', 'Despliegue OpenClaw (on-prem o nube)', 'Configuración Claude Cowork', 'Conexión de hasta 5 fuentes de datos', 'Gobernanza básica + capacitación'],
    cta: 'Solicitar implementación', featured: true },
  { name: 'Enterprise + CTO', price: '60', desc: 'Implementación completa con gobernanza avanzada y CTO as a Service por 3 meses.',
    includes: ['Todo lo de implementación', 'Fuentes ilimitadas', 'Gobernanza avanzada (linaje, compliance)', 'Integración con dashboards y modelos IA', 'CTO as a Service: 4 sesiones/mes', 'Soporte dedicado por 3 meses'],
    cta: 'Solicitar plan enterprise' },
];

const ctoIncludes = [
  '4 sesiones estratégicas mensuales',
  'Revisión de arquitectura y stack',
  'Asesoría en proveedores y contratos tech',
  'Priorización de iniciativas de datos e IA',
  'Acceso directo al equipo ALLCOM',
];

const tagColor = (tag) => tag === 'OpenClaw' ? 'bg-emerald-50 text-emerald-600' : tag === 'Cowork' ? 'bg-violet-50 text-violet-600' : 'bg-sky-50 text-[#0EA5E9]';

export default function OpenClawPage() {
  return (
    <div>
      {/* 1. HERO */}
      <div className="relative overflow-hidden bg-[#050E1F] text-white">
        <img src="/allcom_openclaw_hero.webp" alt="OpenClaw" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.35 }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(5,14,31,0.80) 0%, rgba(5,14,31,0.50) 50%, rgba(5,14,31,0.80) 100%)' }} />
        <div className="absolute inset-0 noise-texture" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-3xl">
            <AuthorityBadge icon={Shrimp}>OPENCLAW / COWORK · PARTNER OFICIAL EN CHILE</AuthorityBadge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-semibold tracking-tight leading-tight mb-2" style={{fontFamily:'Syne,sans-serif'}}>
              El agente de IA que opera tu empresa
            </h1>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight mb-6" style={{fontFamily:'Syne,sans-serif'}}>
              <span className="text-gradient-accent">mientras tú decides.</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-4">
              OpenClaw es un agente autónomo open-source que centraliza datos, automatiza procesos y trabaja 24/7 en tu infraestructura. Claude Cowork es el agente colaborativo de Anthropic que planifica, ejecuta y completa tareas complejas en tu escritorio como un compañero de trabajo real.
            </p>
            <p className="text-sm text-slate-400 leading-relaxed mb-8">
              En ALLCOM implementamos ambos: OpenClaw para tu operación de datos y Claude Cowork para la productividad de tu equipo. Lo integramos en máquinas físicas o en la nube. OpenClaw es gratuito. Cowork requiere suscripción Anthropic (tokens del cliente).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
              {[
                'OpenClaw: agente autónomo que piensa, decide y ejecuta.',
                'Claude Cowork: tu compañero de trabajo virtual con IA.',
                'Open-source + Enterprise. Tus datos bajo tu control.',
                'Integración física (on-premise) o en la nube.',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5 text-xs text-slate-400"><CheckCircle className="w-4 h-4 text-[#0EA5E9] shrink-0" strokeWidth={2} />{item}</div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-[0_10px_24px_rgba(14,165,233,0.35)] transition-all">Agendar diagnóstico <ArrowRight className="w-4 h-4" /></Link>
              <a href="https://wa.me/56975294340?text=Hola,%20me%20interesa%20OpenClaw%20/%20Cowork" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold text-emerald-400 border border-emerald-400/30 rounded-xl hover:bg-emerald-400/10 transition-colors"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
          </div>
        </div>
      </div>

      {/* 2. QUÉ ES */}
      <Section>
        <div className="max-w-3xl mx-auto">
          <SectionTitle badge="QUÉ ES" title="OpenClaw / Claude Cowork" center />
          <div className="space-y-4 text-sm sm:text-base text-[#64748B] leading-relaxed text-center">
            <p><span className="font-semibold text-[#0A2540]">OpenClaw</span> es una plataforma open-source que funciona como agente autónomo: centraliza datos de múltiples fuentes, gobierna su calidad y los activa para análisis, IA y toma de decisiones. Se controla desde WhatsApp o Telegram y trabaja 24/7 sin intervención humana.</p>
            <p><span className="font-semibold text-[#0A2540]">Claude Cowork</span> es el modo de colaboración autónoma de Anthropic. A diferencia de un chatbot, Cowork planifica tareas complejas, las ejecuta paso a paso, accede a archivos, crea documentos, investiga y reporta resultados — como un compañero de trabajo real que no necesita supervisión constante.</p>
            <p>En ALLCOM combinamos ambas plataformas para cubrir toda tu operación: datos e infraestructura con OpenClaw, productividad y colaboración con Claude Cowork. <span className="text-[#0A2540] font-medium">OpenClaw es gratuito y open-source. Claude Cowork requiere suscripción Anthropic (tokens del cliente).</span></p>
          </div>
        </div>
      </Section>

      {/* 3. BENEFICIOS CLAVE */}
      <Section alt>
        <SectionTitle badge="BENEFICIOS" title="Beneficios clave" center />
        <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {benefits.map((b, i) => (
            <motion.div key={i} variants={fadeUp} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <b.icon className="w-6 h-6 text-[#0A2540]" strokeWidth={1.6} />
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${tagColor(b.tag)}`}>{b.tag}</span>
              </div>
              <h3 className="text-sm font-semibold text-[#0A2540] mb-2" style={{fontFamily:'Syne,sans-serif'}}>{b.title}</h3>
              <p className="text-xs text-[#64748B] leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* 4. LO QUE RESUELVE */}
      <Section>
        <SectionTitle badge="AGENTES AUTÓNOMOS" title="Lo que OpenClaw / Cowork resuelve por ti" subtitle="No son chatbots. Son agentes que trabajan 24/7: uno centraliza y gobierna tus datos, el otro ejecuta tareas de conocimiento como un compañero de trabajo real." center />
        <motion.div variants={stagger} className="grid sm:grid-cols-2 gap-4 max-w-5xl mx-auto">
          {agentFeatures.map((f, i) => (
            <motion.div key={i} variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-[10px] font-bold text-slate-300" style={{fontFamily:'JetBrains Mono,monospace'}}>0{i+1}</span>
                <f.icon className="w-5 h-5 text-[#0A2540]" strokeWidth={1.6} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-semibold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>{f.title}</h4>
                  <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-semibold ${tagColor(f.tag)}`}>{f.tag}</span>
                </div>
                <p className="text-xs text-[#64748B] leading-relaxed">{f.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* 5. PARA QUIÉN */}
      <Section alt>
        <SectionTitle badge="INDUSTRIAS" title="Para quién es" center />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
          {industries.map((ind, i) => (
            <div key={i} className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm text-center">
              <ind.icon className="w-6 h-6 text-[#0A2540] mx-auto mb-3" strokeWidth={1.6} />
              <h3 className="text-xs font-semibold text-[#0A2540] mb-1.5" style={{fontFamily:'Syne,sans-serif'}}>{ind.title}</h3>
              <p className="text-[11px] text-[#64748B] leading-relaxed">{ind.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 6. CÓMO LO IMPLEMENTAMOS */}
      <Section>
        <SectionTitle badge="METODOLOGÍA" title="Cómo lo implementamos" center />
        <div className="max-w-3xl mx-auto space-y-4">
          {steps.map((s, i) => (
            <motion.div key={i} variants={fadeUp} className="flex items-start gap-5 p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-[#0A2540] text-white text-xs font-bold flex items-center justify-center shrink-0" style={{fontFamily:'JetBrains Mono,monospace'}}>{s.step}</div>
              <div>
                <h4 className="text-sm font-semibold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>{s.title}</h4>
                <p className="text-xs text-[#64748B] mt-1 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Mosaic */}
      <Section alt>
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-[10px] text-slate-400 uppercase tracking-[0.18em] mb-4">Data lakes, dashboards y agentes implementados con OpenClaw / Cowork</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { url: '/allcom_ia_dashboards.jpg', caption: 'Dashboards' },
              { url: '/allcom_ia_analytics.jpg', caption: 'Analytics' },
              { url: '/allcom_ia_app_celular.jpg', caption: 'Agentes' },
              { url: '/allcom_ia_produccion.jpg', caption: 'Automatización' },
            ].map((img, i) => (
              <div key={i} className="relative rounded-xl overflow-hidden aspect-[4/3] select-none group">
                <img src={img.url} alt={img.caption} className="w-full h-full object-cover" loading="lazy" draggable="false" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-[10px] text-white font-medium text-center">{img.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 7. PLANES */}
      <Section>
        <SectionTitle badge="PLANES" title="Planes de implementación" center />
        <div className="grid lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, i) => (
            <motion.div key={i} variants={fadeUp} className={`p-6 rounded-2xl border shadow-sm flex flex-col ${plan.featured ? 'border-[#0EA5E9] ring-1 ring-[#0EA5E9]/20 bg-white' : 'border-slate-200 bg-white'}`}>
              {plan.featured && <span className="text-[9px] px-2 py-0.5 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-full font-semibold self-start mb-3 uppercase tracking-wider">Recomendado</span>}
              <h3 className="text-base font-semibold text-[#0A2540] mb-1" style={{fontFamily:'Syne,sans-serif'}}>{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>{plan.price}</span>
                <span className="text-sm text-[#64748B]">UF</span>
              </div>
              <p className="text-[10px] text-[#94A3B8] mb-3">Un solo pago + IVA</p>
              <p className="text-xs text-[#64748B] leading-relaxed mb-4">{plan.desc}</p>
              <ul className="space-y-2 mb-5 flex-1">
                {plan.includes.map((item, j) => (
                  <li key={j} className="flex items-start gap-2 text-xs text-[#1E293B]">
                    <CheckCircle className="w-3.5 h-3.5 text-[#0EA5E9] shrink-0 mt-0.5" strokeWidth={2} />{item}
                  </li>
                ))}
              </ul>
              <Link to="/contacto" className={`w-full text-center py-2.5 text-sm font-semibold rounded-xl transition-colors ${plan.featured ? 'bg-[#0EA5E9] text-white hover:bg-sky-600' : 'bg-slate-100 text-[#0A2540] hover:bg-slate-200'}`}>{plan.cta}</Link>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* 8. CTO AS A SERVICE */}
      <Section alt>
        <div className="max-w-4xl mx-auto grid lg:grid-cols-2 gap-8 items-center">
          <div className="relative rounded-2xl overflow-hidden shadow-xl">
            <img src="/allcom_oficina_providencia_santiago_chile.jpg" alt="ALLCOM Oficina" className="w-full h-full object-cover object-bottom min-h-[280px]" style={{objectPosition:'center 70%'}} loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050E1F]/80 via-[#050E1F]/30 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5">
              <h3 className="text-base font-semibold text-white" style={{fontFamily:'Syne,sans-serif'}}>ALLCOM · CTO as a Service</h3>
              <p className="text-[10px] text-slate-300 mt-1">Andrés Bello 1567 · Providencia · Santiago · Chile</p>
            </div>
          </div>
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 mb-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0EA5E9] bg-sky-50 border border-sky-100 rounded-full"><Users className="w-3.5 h-3.5" />CTO AS A SERVICE</span>
            <h3 className="text-xl font-semibold text-[#0A2540] mb-3" style={{fontFamily:'Syne,sans-serif'}}>¿No tienes CTO? Nosotros lo resolvemos.</h3>
            <p className="text-sm text-[#64748B] leading-relaxed mb-5">Muchas empresas necesitan dirección tecnológica pero no pueden contratar un CTO full-time. En ALLCOM ofrecemos CTO as a Service: un líder tecnológico senior que acompaña a tu empresa en decisiones de arquitectura, datos, IA, seguridad y roadmap tecnológico.</p>
            <ul className="space-y-2 mb-5">
              {ctoIncludes.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-[#1E293B]"><CheckCircle className="w-4 h-4 text-[#0EA5E9] shrink-0" strokeWidth={2} />{item}</li>
              ))}
            </ul>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="text-xs text-[#64748B]">Desde</span>
              <span className="text-2xl font-bold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>15</span>
              <span className="text-sm text-[#64748B]">UF/mes</span>
            </div>
            <Link to="/contacto" className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg">Consultar disponibilidad <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </Section>

      {/* 9. BADGES */}
      <div className="bg-white border-y border-slate-100 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[10px] text-slate-400 uppercase tracking-[0.18em] mb-4">Stack tecnológico</p>
          <div className="flex justify-center items-center gap-4 flex-wrap">
            {['OpenClaw', 'Claude Cowork', 'Anthropic', 'Apache Iceberg', 'Apache Spark', 'dbt', 'ALLCOM IA Studio'].map((name, i) => (
              <div key={i} className="px-4 py-2 rounded-lg bg-slate-50 border border-slate-100">
                <span className="text-xs font-semibold text-[#64748B]">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 10. CTA FINAL */}
      <Section>
        <div className="max-w-3xl mx-auto">
          <div className="p-8 sm:p-10 rounded-2xl bg-[#0A2540] text-white text-center">
            <h2 className="text-2xl sm:text-3xl font-semibold mb-3" style={{fontFamily:'Syne,sans-serif'}}>¿Quieres agentes de IA trabajando para tu empresa?</h2>
            <p className="text-sm text-slate-400 mb-6">OpenClaw + Claude Cowork. Datos centralizados, tareas automatizadas, equipo potenciado.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg">Agendar diagnóstico <ArrowRight className="w-4 h-4" /></Link>
              <a href="https://wa.me/56975294340?text=Hola,%20me%20interesa%20OpenClaw%20/%20Cowork" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold text-emerald-400 border border-emerald-400/30 rounded-xl hover:bg-emerald-400/10"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
            <p className="text-[10px] text-slate-500 mt-3">Respuesta inicial en menos de 24 horas</p>
          </div>
        </div>
      </Section>
    </div>
  );
}
